/** Map BusinessHealthAssessmentForm categoryScores → HealthScoreCard domain keys */
export function mapAssessmentToHealthCardScores(categoryScores) {
  if (!categoryScores || typeof categoryScores !== 'object') return {};
  const avg = (a, b) => Math.round(((Number(a) || 0) + (Number(b) || 0)) / 2);
  return {
    finance: { score: Number(categoryScores.financial) || 0 },
    sales: { score: avg(categoryScores.sales, categoryScores.innovation) },
    operations: { score: avg(categoryScores.operations, categoryScores.strategy) },
    hr: { score: avg(categoryScores.customer, categoryScores.leadership) },
    compliance: { score: Number(categoryScores.compliance) || 0 },
  };
}

/** Merge assessment-derived scores into healthData shape { domain: { score, details? } } */
export function mergeHealthDataFromAssessment(healthData, assessmentPayload) {
  const base = healthData && typeof healthData === 'object' ? { ...healthData } : {};
  const mapped = mapAssessmentToHealthCardScores(assessmentPayload?.categoryScores);
  Object.entries(mapped).forEach(([key, v]) => {
    base[key] = { ...(base[key] || {}), score: v.score };
  });
  if (assessmentPayload?.overallScore != null) {
    base.overallScore = assessmentPayload.overallScore;
    base.lastUpdated = assessmentPayload.lastUpdated || base.lastUpdated;
  }
  return base;
}

export function appendTrendSnapshot(trendData, snapshot) {
  const next = trendData && typeof trendData === 'object' ? { ...trendData } : {};
  const metrics = [
    ['overall', snapshot.overall],
    ['finance', snapshot.finance],
    ['sales', snapshot.sales],
    ['operations', snapshot.operations],
    ['hr', snapshot.hr],
    ['compliance', snapshot.compliance],
  ];
  metrics.forEach(([metricKey, raw]) => {
    const value = typeof raw === 'object' && raw !== null && 'score' in raw ? raw.score : raw;
    if (value == null || Number.isNaN(Number(value))) return;
    const num = Number(value);
    const prev = Array.isArray(next[metricKey]) ? [...next[metricKey]] : [];
    prev.push({ date: new Date().toISOString(), value: num });
    next[metricKey] = prev.slice(-36);
  });
  return next;
}

const uid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `item_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

/** Baseline diagnostic items per domain (sync with DiagnosticChecklist category keys). */
export function getDefaultDiagnosticDomains() {
  return {
    finance: [
      {
        id: uid(),
        question: 'Are cash flow and runway documented and reviewed monthly?',
        priority: 'high',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
      {
        id: uid(),
        question: 'Do you have separated business and personal finances?',
        priority: 'high',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
      {
        id: uid(),
        question: 'Are key financial ratios (margin, liquidity) tracked?',
        priority: 'medium',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
    ],
    sales: [
      {
        id: uid(),
        question: 'Is there a defined sales pipeline with conversion stages?',
        priority: 'high',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
      {
        id: uid(),
        question: 'Are marketing channels measured for ROI?',
        priority: 'medium',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
      {
        id: uid(),
        question: 'Is your ideal customer profile documented?',
        priority: 'medium',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
    ],
    operations: [
      {
        id: uid(),
        question: 'Are core processes documented and owned?',
        priority: 'high',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
      {
        id: uid(),
        question: 'Do you have contingency plans for supplier or service disruption?',
        priority: 'medium',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
    ],
    hr: [
      {
        id: uid(),
        question: 'Are roles, responsibilities, and reporting lines clear?',
        priority: 'high',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
      {
        id: uid(),
        question: 'Is there a basic HR policy (leave, conduct, remote work)?',
        priority: 'medium',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
    ],
    compliance: [
      {
        id: uid(),
        question: 'Are licences, registrations, and tax filings current?',
        priority: 'high',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
      {
        id: uid(),
        question: 'Are contracts with customers and vendors reviewed on a schedule?',
        priority: 'medium',
        status: 'not-started',
        notes: '',
        createdAt: new Date().toISOString(),
      },
    ],
  };
}

export function isDiagnosticChecklistEmpty(domains) {
  if (!domains || typeof domains !== 'object') return true;
  const keys = ['finance', 'sales', 'operations', 'hr', 'compliance'];
  return keys.every(k => !Array.isArray(domains[k]) || domains[k].length === 0);
}

/** Aligns with DiagnosticChecklist yes/partial/no: "done" = yes (or legacy completed). */
export function mapDiagnosticResponseToStatus(r) {
  if (r === 'yes') return 'completed';
  if (r === 'partial') return 'in-progress';
  if (r === 'no') return 'not-started';
  return 'not-started';
}

/** Derive yes/partial/no for display; supports legacy status-only items. */
export function getEffectiveDiagnosticResponse(item) {
  if (item?.response === 'yes' || item?.response === 'partial' || item?.response === 'no') {
    return item.response;
  }
  if (item?.status === 'completed') return 'yes';
  if (item?.status === 'in-progress') return 'partial';
  return '';
}

export function computeChecklistCompletion(domains) {
  const keys = ['finance', 'sales', 'operations', 'hr', 'compliance'];
  let total = 0;
  let done = 0;
  keys.forEach(k => {
    const items = Array.isArray(domains?.[k]) ? domains[k] : [];
    total += items.length;
    done += items.filter(i => getEffectiveDiagnosticResponse(i) === 'yes').length;
  });
  return { total, done, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
}

export const ASSESSMENT_CATEGORY_LABELS = {
  strategy: 'Strategy & Vision',
  financial: 'Financial Health',
  sales: 'Sales & Marketing',
  operations: 'Operations & Processes',
  customer: 'Customer Experience',
  leadership: 'Leadership & Team',
  compliance: 'Compliance & Risk',
  innovation: 'Innovation & Growth',
};
